// Knowledge Base view — categorized articles with search and browse.
// migrated 2026-04-27 to SDK route workspaceProject.documents.kb

import { KB_CATEGORIES } from '../../state.js'
import { _navigate } from './Main.js'
import { getSDK } from '../../../shared/functions/sdk.js'

const _filteredArticles = (s) => {
  const cat = s.root.selectedCategory || 'all'
  const q = (s.root.kbQuery || '').toLowerCase().trim()
  let articles = s.root.kbArticles || []
  if (cat !== 'all') articles = articles.filter((a) => a.category === cat)
  if (q) {
    articles = articles.filter(
      (a) =>
        (a.title || '').toLowerCase().includes(q) ||
        (a.summary || '').toLowerCase().includes(q) ||
        (a.author || '').toLowerCase().includes(q)
    )
  }
  return articles
}

export const KnowledgeBaseMain = {
  extends: 'AppMain',
  padding: '0',
  Header: { extends: 'KbHeader' },
  Body: {
    padding: 'B C',
    overflow: 'auto',
    flow: 'y',
    gap: 'A',

    KbArticleGrid: {}
  }
}

export const KbHeader = {
  extends: 'DocsContentHeader',

  LeftGroup: {
    Title: {
      text: (el, s) => {
        const cat = KB_CATEGORIES.find((c) => c.key === s.root.selectedCategory)
        return cat?.label || 'Knowledge Base'
      }
    },
    Count: {
      text: (el, s) => {
        const n = _filteredArticles(s).length
        return `${n} article${n === 1 ? '' : 's'}`
      }
    }
  },

  RightGroup: {
    Search: {
      placeholder: 'Search knowledge base...',
      value: (el, s) => s.root.kbQuery || '',
      onInput: (e, el, s) => {
        s.root.update({ kbQuery: e.target.value })
      }
    },

    PrimaryBtn: {
      text: '+ New article',
      onClick: async (e, el, s) => {
        const cat =
          s.root.selectedCategory === 'all'
            ? 'getting-started'
            : s.root.selectedCategory
        const row = {
          title: 'Untitled article',
          category: cat,
          author: s.root.currentUserEmail || 'Unknown',
          // Populate `author_email` for Phase E ownership-gated RLS (0069).
          author_email: s.root.currentUserEmail || null,
          summary: '',
          content: { blocks: [] }
        }
        let data
        try {
          // migrated 2026-04-27 to SDK route workspaceProject.documents.kb
          const result = await getSDK().execute('workspaceProject.documents.kb', 'create', { payload: row })
          data = result?.data || result
        } catch (err) {
          console.error('[KB] Insert failed:', err)
          return
        }
        if (!data?.id) return
        const articles = s.root.kbArticles || []
        const article = {
          ...row,
          id: data.id,
          updatedAt: new Date().toISOString().slice(0, 10)
        }
        s.root.update({
          kbArticles: [...articles, article],
          editorDoc: {
            id: data.id,
            title: article.title,
            content: { blocks: [] },
            source: 'kb'
          },
          activeSection: 'editor'
        })
        _navigate(el, '/documents/editor')
      }
    }
  }
}

export const KbArticleGrid = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
  gap: 'A',

  childExtends: 'KbArticleCard',
  childrenAs: 'state',
  children: (el, s) => _filteredArticles(s)
}

export const KbArticleCard = {
  extends: 'Link',
  href: (el, s) => `/documents/editor#kb=${encodeURIComponent(s.id || '')}`,
  textDecoration: 'none',
  color: 'inherit',
  flow: 'y',
  gap: 'Z1',
  padding: 'A',
  round: 'A',
  borderWidth: '1px',
  borderColor: 'line',
  borderStyle: 'solid',
  background: 'overlayStrong',
  cursor: 'pointer',
  transition: 'A defaultBezier',
  ':hover': { borderColor: 'title', transform: 'translateY(-2px)' },
  onClick: (e, el, s) => {
    e.preventDefault()
    const content =
      s.content && typeof s.content === 'object' ? s.content : { blocks: [] }
    s.root.update({
      editorDoc: { id: s.id, title: s.title, content, source: 'kb' },
      editorMode: 'view',
      activeSection: 'editor'
    })
    _navigate(el, `/documents/editor#kb=${encodeURIComponent(s.id || '')}`)
  },

  Top: {
    flow: 'x',
    align: 'center left',
    gap: 'Z1',

    Icon: {
      icon: 'book',
      boxSize: 'A2 A2',
      color: 'caption'
    },
    Title: {
      tag: 'h3',
      margin: '0',
      fontSize: 'A',
      fontWeight: '600',
      color: 'title',
      flex: '1',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      text: (el, s) => s.title || 'Untitled'
    }
  },

  CategoryBadge: {
    tag: 'span',
    fontSize: 'Y1',
    fontWeight: '600',
    padding: 'V Z',
    round: 'Y',
    background: 'overlay',
    color: 'caption',
    textTransform: 'uppercase',
    letterSpacing: '.05em',
    alignSelf: 'flex-start',
    text: (el, s) => {
      const cats = s.root?.kbCategories || []
      const cat = cats.find((c) => c.key === s.category)
      return cat?.label || s.category || ''
    }
  },

  Summary: {
    tag: 'p',
    margin: '0',
    fontSize: 'Z2',
    color: 'caption',
    lineHeight: '1.5',
    text: (el, s) => s.summary || 'No description'
  },

  Meta: {
    flow: 'x',
    align: 'center space-between',
    gap: 'Z',
    marginTop: 'Z',
    fontSize: 'Y2',
    color: 'caption',

    Author: { tag: 'span', text: (el, s) => s.author || '' },
    Updated: {
      tag: 'span',
      text: (el, s) => (s.updatedAt ? `Updated ${s.updatedAt}` : '')
    }
  }
}
